import { useContext, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { isTFolder, TFolder, TPath, useFolder } from '@modules/items';
import { SignInContext } from '@modules/users';
import { StatusLabel } from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';
import { NoPage } from '@shared/pages';

import ChangeAssessmentScreen from '../components/ChangeAssessmentScreen';
import { getChangeDashboardPath, getChangeDetailPath, isChangeMockVisible } from '../helpers';
import type { TChangeDetailLocationState } from '../types';

const buildFullPath = (folder: TFolder, changeNumber: string, changeTitle: string, productId?: string): TPath => [
  ...folder.resolvedPath,
  { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
  {
    _id: 'changes',
    name: 'Changes',
    accessRight: folder.accessRight,
    href: getChangeDashboardPath(productId),
  },
  {
    _id: changeNumber,
    name: changeTitle,
    accessRight: folder.accessRight,
    href: getChangeDetailPath(changeNumber, productId),
  },
];

const MockChangeDetail = () => {
  const { productId, changeNumber = '' } = useParams();
  const { state } = useLocation();
  const locationState = state as TChangeDetailLocationState | null;
  const { organization } = useContext(SignInContext);
  const config = organization.mock?.change;
  const folderId = productId ?? organization.folders.qm;
  const isVisible = isChangeMockVisible(config) && Boolean(folderId);
  const existingChange = config?.changes.find((change) => change.changeNumber === changeNumber);
  const changeRequest =
    locationState?.changeRequest?.changeNumber === changeNumber ? locationState.changeRequest : undefined;
  const changeTitle = changeRequest?.changeTitle ?? existingChange?.changeTitle ?? changeNumber;

  const { error, data } = useFolder(folderId, { enabled: isVisible });

  useDocumentTitle(changeTitle || 'Change');

  const scope = productId ? 'product' : 'company';
  const fullPath = useMemo(
    () => (data && isTFolder(data) ? buildFullPath(data as TFolder, changeNumber, changeTitle, productId) : undefined),
    [changeNumber, changeTitle, data, productId]
  );

  if (!isVisible || !config || (!existingChange && !changeRequest)) return <NoPage />;

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading change..."
      error={error}
      errorMessage="Loading change failed!"
      render={(resolvedData) =>
        isTFolder(resolvedData) && fullPath ? (
          <ChangeAssessmentScreen
            folder={resolvedData as TFolder}
            fullPath={fullPath}
            config={config}
            scope={scope}
            changeRequest={changeRequest}
            existingChange={existingChange}
            startAiAssessment={locationState?.startAiAssessment}
          />
        ) : (
          <NoPage />
        )
      }
    />
  );
};

export default MockChangeDetail;
